import { useApi } from 'ApiHooks'
import ModalConfirmacion from 'Components/ModalConfirmacion'
import ModalEditarDomicilio from 'Components/ModalEditarDomicilio'
import { useBlockUI } from 'Context/BlockUIContext'
import { useErrorModal } from 'Context/ErrorModalContext'
import { Domicilio } from 'Interfaces/Domicilio'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Button, Modal, Table, Card, Alert } from 'react-bootstrap'
import { useIsMobile } from 'utils/Utils'

interface ModalDomiciliosProps {
    showModalDomicilios: boolean
    setShowModalDomicilios: Dispatch<SetStateAction<boolean>>
    onClose: () => void
    onSelect: (domicilio: Domicilio) => void
    obtenerDomicilios: any
    domiciliosData: Domicilio[]
    hiddenSelectButton?: boolean
    faltaDatoDomicilio?: boolean
}

const ModalDomicilios = ({
    showModalDomicilios,
    onClose,
    onSelect,
    obtenerDomicilios,
    domiciliosData,
    setShowModalDomicilios,
    hiddenSelectButton,
    faltaDatoDomicilio
}: ModalDomiciliosProps) => {

    const isMobile = useIsMobile()
    const api = useApi()
    const { showBlock, hideBlock } = useBlockUI()
    const { showError } = useErrorModal()
    const [domicilios, setDomicilios] = useState<Domicilio[]>([])
    const [showEditar, setShowEditar] = useState(false)
    const [domicilioEditando, setDomicilioEditando] = useState<Domicilio | null>(null)
    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const [domicilioAEliminar, setDomicilioAEliminar] = useState<any | null>(null)

    const handleDeleteClick = (d: Domicilio) => {
        setDomicilioAEliminar(d)
        setShowConfirmDelete(true)
    }

    const confirmarBorrado = async () => {
        if (!domicilioAEliminar) return
        try {
            showBlock('Eliminando domicilio...')
            await api.deleteDomicilio(domicilioAEliminar.generadorId!)
            setShowConfirmDelete(false)
            onClose()
            obtenerDomicilios().then(() => {
                setShowModalDomicilios(true)
            })
        } catch (e: any) {
            showError({
                title: 'Error',
                message: e?.response?.data?.message
            })
        } finally {
            hideBlock()
        }
    }

    const handleEdit = (d: Domicilio) => {
        setDomicilioEditando(d)
        setShowEditar(true)
    }

    const handleSave = async (domicilio: any) => {
        try {
            showBlock('Guardando domicilio...')
            await api.updateDomicilio({
                Id: domicilio?.generadorId,
                Calle: domicilio?.calle,
                Numero: domicilio?.numero,
                Piso: domicilio?.piso,
                Departamento: domicilio?.departamento,
                Provincia: domicilio?.provincia,
                Localidad: domicilio?.localidad,
                Region: domicilio?.region,
                Telefono: domicilio?.telefono,
                CodigoPostal: domicilio?.codigoPostal,
                Referencias: domicilio?.referencias,
                CodigoSucursalCorreo: domicilio?.codigoSucursalCorreo,
                localidadSucursal: domicilio?.localidadSucursal,
            })
            setShowEditar(false)
            onClose()
            obtenerDomicilios().then(() => {
                setShowModalDomicilios(true)
            })
        } catch (e: any) {
            showError({
                title: 'Error',
                message: e?.response?.data?.message
            })
        } finally {
            hideBlock()
        }
    }

    const handleCreate = async (domicilio: Domicilio) => {
        try {
            showBlock('Creando domicilio...')
            await api.createDomicilio(domicilio)
            setShowEditar(false)
            onClose()
            obtenerDomicilios().then(() => {
                setShowModalDomicilios(true)
            })
        } catch (e: any) {
            showError({
                title: 'Error',
                message: e?.response?.data?.message
            })
        } finally {
            hideBlock()
        }
    }

    const domicilioIncompleto = (d: any) =>
        [
            d?.calle,
            d?.numero,
            d?.codigoPostal,
            d?.telefono,
            d?.provincia,
            d?.localidad,
        ].some(v => v == null || v === '')


    useEffect(() => {
        if (showModalDomicilios) {
            setDomicilios(domiciliosData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps   
    }, [showModalDomicilios])

    return (
        <>
            <Modal
                show={showModalDomicilios}
                onHide={faltaDatoDomicilio ? undefined : onClose}
                size="lg"
                centered
                scrollable
                keyboard={faltaDatoDomicilio ? false : true}
                style={{ backgroundColor: 'rgba(1,1,1,0.5)' }}

            >
                <Modal.Header closeButton={false}>
                    <div className="w-100 d-flex align-items-center justify-content-between">
                        <Modal.Title style={{ color: 'white', fontWeight: 'bold' }}>
                            Domicilios
                        </Modal.Title>

                        <Button variant="primary" size="sm" onClick={() => {
                            setDomicilioEditando(null)
                            setShowEditar(true)
                        }}>
                            + Nuevo
                        </Button>
                    </div>
                </Modal.Header>


                <Modal.Body>
                    {faltaDatoDomicilio && (
                        <Alert variant="danger" className="mb-4">
                            <div style={{ fontSize: 20, fontWeight: 'bold' }}>Importante.</div>
                            <div style={{ fontSize: 15, marginTop: 10 }}>
                                Es necesario que actualice su domicilio de retiro de las guías de pedido. En caso de contar con más de una oficina, podrá ingresar varios domicilios para gestionar sus despachos.

                            </div>
                            <div style={{ fontSize: 15, marginTop: 15, marginBottom: 5 }}>
                                Si trabaja con el envío Andreani, debe seleccionar una sucursal de Andreani próxima a cada domicilio registrado. No está obligado a realizar las entregas en la sucursal seleccionada; sin embargo, en caso de devolución de las mismas, estas retornarán al domicilio de origen o, en su defecto, a la sucursal de preferencia vinculada al mismo.
                            </div>
                        </Alert>
                    )}
                    {domicilios?.length === 0 ? (
                        <div className="text-center text-muted p-4">
                            No hay domicilios cargados
                        </div>
                    ) : isMobile ?
                        (
                            domicilios?.map(d => {
                                const incompleto = domicilioIncompleto(d)

                                return (
                                    <Card
                                        key={d.id}
                                        className={`mb-2 shadow-sm ${incompleto ? 'border-danger' : 'border-0'}`}
                                    >
                                        <Card.Body className="p-2">
                                            <strong className={incompleto ? 'text-danger' : ''}>
                                                {d.calle} {d.numero}
                                            </strong>

                                            <div className="small text-muted">
                                                {d.localidad} – {d.provincia}
                                            </div>

                                            {d.telefono && (
                                                <div className="small">📞 {d.telefono}</div>
                                            )}

                                            {incompleto && (
                                                <div className="small text-danger mt-1">
                                                    ⚠ Faltan datos obligatorios
                                                </div>
                                            )}

                                            <div className="d-flex justify-content-end mt-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    className="mr-2"
                                                    onClick={() => handleEdit(d)}
                                                >
                                                    Editar
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    className="mr-2"
                                                    onClick={() => handleDeleteClick(d)}
                                                >
                                                    Borrar
                                                </Button>

                                                {!hiddenSelectButton && <Button
                                                    size="sm"
                                                    variant="primary"
                                                    disabled={incompleto}
                                                    onClick={() => onSelect(d)}
                                                >
                                                    Seleccionar
                                                </Button>}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )
                            })
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0">
                                    <thead className="thead-light">
                                        <tr>
                                            <th>Domicilio</th>
                                            <th>Localidad</th>
                                            <th>Provincia</th>
                                            <th>Teléfono</th>
                                            <th className="text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {domicilios?.map(d => {
                                            const incompleto = domicilioIncompleto(d)

                                            return (
                                                <tr key={d.id} className={incompleto ? 'table-danger' : ''}>
                                                    <td>
                                                        {d.calle} {d.numero}
                                                        {d.piso && ` Piso ${d.piso}`}
                                                        {d.departamento && ` Dto ${d.departamento}`}

                                                        {incompleto && (
                                                            <div className="small text-danger">
                                                                ⚠ Datos incompletos
                                                            </div>
                                                        )}
                                                    </td>

                                                    <td>{d.localidad}</td>
                                                    <td>{d.provincia}</td>
                                                    <td>{d.telefono || '-'}</td>

                                                    <td className="text-right">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            className="mr-2"
                                                            onClick={() => handleEdit(d)}
                                                        >
                                                            Editar
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            className="mr-2"
                                                            onClick={() => handleDeleteClick(d)}
                                                        >
                                                            Borrar
                                                        </Button>

                                                        {!hiddenSelectButton && <Button
                                                            size="sm"
                                                            variant="primary"
                                                            disabled={incompleto}
                                                            onClick={() => onSelect(d)}
                                                        >
                                                            Seleccionar
                                                        </Button>}
                                                    </td>
                                                </tr>
                                            )
                                        })}

                                    </tbody>
                                </Table>
                            </div>
                        )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            <ModalEditarDomicilio
                show={showEditar}
                domicilio={domicilioEditando}
                setDomicilio={setDomicilioEditando}
                onClose={() => setShowEditar(false)}
                onSave={(domEdit) => {
                    handleSave(domEdit)
                    setDomicilioEditando(null)
                }}
                onCreate={(domicilio: Domicilio) => {
                    handleCreate(domicilio)
                    setDomicilioEditando(null)

                }}
            />

            <ModalConfirmacion
                show={showConfirmDelete}
                title="Eliminar domicilio"
                message={`¿Seguro que querés eliminar el domicilio de ${domicilioAEliminar?.calle} ${domicilioAEliminar?.numero}?`}
                confirmText="Eliminar"
                variant="danger"
                onCancel={() => setShowConfirmDelete(false)}
                onConfirm={confirmarBorrado}
            />

        </>
    )
}

export default ModalDomicilios
